import React, { useMemo } from 'react';
import { Layout, Card, Typography, Button, Spin, Input, message as antdMessage } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import bg from '../../../assets/images/background/background_login.jpg';
import { appVersion } from '../../../config/config';
import LanguageSwitcher from '../../../components/auth/LanguageSwitcher';
import { useAuthApi } from '../../../api/auth/auth';
import { useApiClient } from '../../../api/apiClients';

const { Title, Paragraph } = Typography;
const { Content, Footer } = Layout;

export default function EmailConfirm() {
  const { t } = useTranslation();
  const { resendConfirmationEmail } = useAuthApi();
  const identityClient = useApiClient('identity');
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const status = (params.get('status') || '').toLowerCase();

  // Initialize loading to true if we have a token (will process it)
  // or if we have no status/token yet (initial state)
  const [loading, setLoading] = React.useState<boolean>(!!token && !status);
  const [isSuccess, setIsSuccess] = React.useState<boolean>(false);
  const [info, setInfo] = React.useState<string>('');
  const [resendEmail, setResendEmail] = React.useState<string>('');
  const [resendLoading, setResendLoading] = React.useState<boolean>(false);
  const [resendDone, setResendDone] = React.useState<boolean>(false);

  const handleResend = async () => {
    if (!resendEmail.trim()) {
      antdMessage.warning('Veuillez saisir votre adresse email.');
      return;
    }
    setResendLoading(true);
    try {
      await resendConfirmationEmail(resendEmail.trim());
    } catch {
      // réponse générique dans tous les cas
    } finally {
      setResendLoading(false);
      setResendDone(true);
      antdMessage.success("Si un compte existe avec cet email, un nouveau lien d'activation a été envoyé. Vérifiez votre boîte de réception.");
    }
  };

  const palette = useMemo(
    () => ({
      primary: '#2D9CC9',
      secondary: '#8BBE1E',
      background: '#031323',
      surface: '#f5f9ff',
      outline: 'rgba(41, 146, 191, 0.22)',
      textPrimary: '#0c1324',
      textMuted: 'rgba(14, 21, 34, 0.62)',
    }),
    []
  );

  // Monitor location changes and navigation events
  React.useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      // Handle popstate if needed
    };

    const handleHashChange = () => {
      // Handle hashchange if needed
    };

    // Intercept pushState and replaceState to prevent redirects
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    let allowNavigation = false; // Flag to allow navigation only when user clicks button

    window.history.pushState = function(...args) {
      const targetUrl = args[2] ? String(args[2]) : null;
      const isLeavingEmailConfirm = targetUrl && !targetUrl.includes('/callback/email-confirm');

      // Prevent navigation away from email confirm page unless explicitly allowed
      if (isLeavingEmailConfirm && !allowNavigation && window.location.pathname === '/callback/email-confirm') {
        return; // Block the navigation
      }

      return originalPushState.apply(window.history, args);
    };

    window.history.replaceState = function(...args) {
      const targetUrl = args[2] ? String(args[2]) : null;
      const isLeavingEmailConfirm = targetUrl && !targetUrl.includes('/callback/email-confirm');

      // Prevent navigation away from email confirm page unless explicitly allowed
      if (isLeavingEmailConfirm && !allowNavigation && window.location.pathname === '/callback/email-confirm') {
        // Don't block replaceState if it's just updating the URL without changing pathname
        // But block if it's trying to change to a different path
        if (targetUrl && targetUrl !== window.location.pathname + window.location.search) {
          return; // Block the navigation
        }
      }

      return originalReplaceState.apply(window.history, args);
    };

    // Expose function to allow navigation (for the button click)
    (window as any).__allowEmailConfirmNavigation = () => {
      allowNavigation = true;
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handleHashChange);

    // Monitor for redirects (interval removed, navigation is blocked above)

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  React.useEffect(() => {
    const message = params.get('message') || '';
    
    if (status) {
      if (status === 'pending') {
        // Pending status: user just registered, show success state with pending message
        setIsSuccess(true);
        setInfo(message || t('auth.emailConfirm.pending.message'));
      } else {
        setIsSuccess(status === 'success');
        setInfo(message || (status === 'success' ? t('auth.emailConfirm.success.message') : t('auth.emailConfirm.error.message')));
      }
      setLoading(false);
      return;
    }
    
    if (token) {
      setLoading(true);
      identityClient
        .get<{ message?: string }>(`/confirm-email?mode=json&token=${encodeURIComponent(token)}`)
        .then((res) => {
          setIsSuccess(true);
          setInfo(res.data?.message || t('auth.emailConfirm.success.message'));
        })
        .catch((err: any) => {
          setIsSuccess(false);
          const msg = err?.response?.data?.message || err?.response?.data?.error?.message;
          setInfo(msg || t('auth.emailConfirm.error.message'));
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setIsSuccess(false);
      setInfo(t('auth.emailConfirm.error.message'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const getTitle = () => {
    if (status === 'pending') {
      return t('auth.emailConfirm.pending.title');
    }
    return isSuccess ? t('auth.emailConfirm.success.title') : t('auth.emailConfirm.error.title');
  };
  
  const title = getTitle();

  return (
    <Layout
      style={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: palette.background,
      }}
    >
      {/* Language Switcher - Top Right */}
      <div
        style={{
          position: 'absolute',
          top: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <LanguageSwitcher variant="dark" />
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(135deg, rgba(41,146,191,0.24), rgba(8,44,68,0.55)), url("${bg}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.92)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 360,
          height: 360,
          borderRadius: '50%',
          top: '-120px',
          left: '-120px',
          background: 'radial-gradient(circle, rgba(139,190,30,0.36), transparent 70%)',
          filter: 'blur(16px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: 420,
          height: 420,
          borderRadius: '50%',
          bottom: '-180px',
          right: '-120px',
          background: 'radial-gradient(circle, rgba(41,146,191,0.32), transparent 70%)',
          filter: 'blur(22px)',
        }}
      />
      <Content
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px 16px',
          position: 'relative',
        }}
      >
        <Card
          bordered={false}
          style={{
            backgroundColor: palette.surface,
            borderRadius: 32,
            margin: '20px',
            width: '100%',
            maxWidth: '520px',
            boxShadow: '0 28px 65px rgba(6,24,38,0.32)',
            backdropFilter: 'blur(16px)',
            border: `1px solid rgba(255,255,255,0.75)`,
            overflow: 'hidden',
          }}
          bodyStyle={{
            padding: '42px 36px',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <span
            style={{
              position: 'absolute',
              inset: '12px',
              borderRadius: 22,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.45), rgba(255,255,255,0.15))',
              border: `1px solid ${palette.outline}`,
              zIndex: -1,
            }}
          />
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <img
              src="/images/logo_120x64.png"
              alt="Tilmicode"
              style={{ height: 64 }}
            />
          </div>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            {loading ? (
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 48, color: palette.primary }} spin />}
              />
            ) : isSuccess ? (
              <CheckCircleOutlined
                style={{
                  fontSize: 64,
                  color: palette.secondary,
                  marginBottom: '16px',
                }}
              />
            ) : info ? (
              <CloseCircleOutlined
                style={{
                  fontSize: 64,
                  color: '#ff4d4f',
                  marginBottom: '16px',
                }}
              />
            ) : null}
          </div>
          <Title
            level={2}
            style={{
              textAlign: 'center',
              marginBottom: 12,
              color: palette.textPrimary,
              fontWeight: 800,
            }}
          >
            {title}
          </Title>
          <Paragraph
            style={{
              textAlign: 'center',
              marginBottom: 32,
              color: palette.textMuted,
              fontSize: 15,
              lineHeight: 1.6,
              minHeight: '48px',
            }}
          >
            {loading ? t('auth.emailConfirm.confirming') : info}
          </Paragraph>
          {!loading && (
            <>
              <Button
                type="primary"
                size="large"
                block
                onClick={() => {
                  (window as any).__allowEmailConfirmNavigation?.();
                  window.location.href = '/login';
                }}
                style={{
                  borderRadius: 999,
                  height: 52,
                  border: 'none',
                  background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                  boxShadow: '0 24px 45px rgba(45,156,201,0.35)',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                }}
              >
                {t('auth.emailConfirm.goToLogin')}
              </Button>

              {/* Bouton resend — affiché uniquement en cas d'erreur (token invalide/expiré) */}
              {!isSuccess && !resendDone && (
                <div style={{ marginTop: 24 }}>
                  <Typography.Text style={{ display: 'block', textAlign: 'center', color: palette.textMuted, fontSize: 14, marginBottom: 12 }}>
                    Votre lien a expiré ?
                  </Typography.Text>
                  <Input
                    prefix={<MailOutlined style={{ color: palette.textMuted }} />}
                    placeholder="Votre adresse email"
                    size="large"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    onPressEnter={handleResend}
                    style={{ borderRadius: 12, marginBottom: 10, border: `1px solid ${palette.outline}` }}
                  />
                  <Button
                    size="large"
                    block
                    loading={resendLoading}
                    onClick={handleResend}
                    style={{ borderRadius: 999, height: 48, border: `1px solid ${palette.primary}`, color: palette.primary, fontWeight: 600 }}
                  >
                    Renvoyer l'email d'activation
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </Content>
      <Footer style={{ background: 'transparent', position: 'relative', zIndex: 1 }}>
        <div
          style={{
            margin: '0 auto',
            padding: '10px 24px',
            borderRadius: 999,
            background: 'rgba(7, 25, 40, 0.82)',
            border: '1px solid rgba(255,255,255,0.32)',
            color: 'rgba(255,255,255,0.92)',
            fontSize: 12,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            maxWidth: 360,
            textAlign: 'center',
          }}
        >
          {t('auth.login.footer', { year: new Date().getFullYear(), version: appVersion || 'dev' })}
        </div>
      </Footer>
    </Layout>
  );
}
